import { FileText, ArrowLeft, ArrowRight, Check, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../config/company';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { LEGAL_CONTENT } from '../config/legalContent';

export default function TermsOfService() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const lang = isAr ? 'ar' : 'en';
  const BackArrow = isAr ? ArrowRight : ArrowLeft;

  const T = LEGAL_CONTENT.terms;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute top-20 end-1/4 w-[400px] h-[400px] ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/5'} rounded-full blur-[100px]`} />
      </div>

      {/* Compact Header */}
      <section className="relative pt-10 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#1E9BD9] hover:underline mb-6 text-sm" data-testid="terms-back-home">
            <BackArrow className="h-4 w-4" />
            {t('pages.backToHome')}
          </Link>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-[#1E9BD9]/10 border border-[#1E9BD9]/20' : 'bg-[#1E9BD9]/10'}`}>
              <FileText className="h-7 w-7 text-[#1E9BD9]" />
            </div>
            <div>
              <h1 className={`text-3xl sm:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} data-testid="terms-title">
                {t('pages.termsTitle')}
              </h1>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                {t('pages.termsSubtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-2xl p-6 md:p-10 ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-[#E2E8F0] shadow-sm'}`}>
            <div className={`flex items-start gap-4 p-4 rounded-xl mb-8 ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className={`text-sm ${isDarkMode ? 'text-amber-200/90' : 'text-[#92400E]'}`}>
                {T.warning[lang]}
              </p>
            </div>

            <div className="space-y-8">
              {T.sections.map((section, index) => (
                <div key={index}>
                  <h3 className={`text-lg font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    <span className="w-8 h-8 bg-[#1E9BD9] text-white rounded-lg flex items-center justify-center text-sm flex-shrink-0">
                      {index + 1}
                    </span>
                    {section[lang].title}
                  </h3>
                  <p className={`leading-relaxed text-sm ms-10 ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>{section[lang].content}</p>
                </div>
              ))}
            </div>

            <div className={`mt-10 pt-8 border-t ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
              <div className="flex items-center gap-2 text-[#10B981] mb-4">
                <Check className="h-5 w-5" />
                <span className="font-medium text-sm">
                  {T.agreement[lang]}
                </span>
              </div>
              <p className={isDarkMode ? 'text-gray-400' : 'text-[#475569]'}>
                {T.contact[lang]}
                <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#1E9BD9] hover:underline">
                  {COMPANY_INFO.email}
                </a>
              </p>
              <p className={`text-sm mt-4 ${isDarkMode ? 'text-gray-600' : 'text-[#94A3B8]'}`}>
                {LEGAL_CONTENT.lastUpdated[lang]}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
